import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import { Checkbox } from '../../../components/ui/Checkbox';
import { supabase } from '../../../lib/supabase';
import StepActions from './StepActions';
import {
  getAvailableSlots,
  isTechnicianOnTimeOff,
  parseTimeString,
} from '../scheduleAvailability';

const ScheduleStep = ({
  data,
  onUpdate,
  onNext,
  onBack,
  isActive = true,
  showBack = true,
  heading = null,
  subheading = null,
}) => {
  const [selectedSlot, setSelectedSlot] = useState(data?.timeSlot || null);
  const [selectedTechnician, setSelectedTechnician] = useState(data?.technician || null);
  const [showTechnicianChoice, setShowTechnicianChoice] = useState(false);
  const [error, setError] = useState('');
  const [appointments, setAppointments] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [timeOffEvents, setTimeOffEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(() => {
    if (data?.timeSlot?.date) {
      const d = new Date(data.timeSlot.date);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const source = data?.timeSlot?.date ? new Date(data.timeSlot.date) : new Date();
    return new Date(source.getFullYear(), source.getMonth(), 1);
  });

  // Récupérer tous les rendez-vous, la liste des techniciens et les absences
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Récupérer tous les techniciens actifs
        const { data: techniciansData, error: techniciansError } = await supabase
          .from('user_profiles')
          .select('id, full_name, specialty, is_active')
          .eq('role', 'technicien')
          .eq('is_active', true);
        
        if (techniciansError) throw techniciansError;
        setTechnicians(techniciansData || []);

        // Récupérer tous les rendez-vous
        const { data: appointmentsData, error: appointmentsError } = await supabase
          .from('appointments')
          .select('scheduled_date, scheduled_time_start, scheduled_time_end, technician')
          .not('scheduled_date', 'is', null)
          .not('scheduled_time_start', 'is', null)
          .not('scheduled_time_end', 'is', null);
        
        if (appointmentsError) throw appointmentsError;
        setAppointments(appointmentsData || []);

        // Récupérer toutes les absences (congés, maladies, etc.)
        const { data: timeoffData, error: timeoffError } = await supabase
          .from('technician_timeoff')
          .select('*');
        
        if (timeoffError) throw timeoffError;
        setTimeOffEvents(timeoffData || []);

      } catch (error) {
        console.error('Erreur lors de la récupération des données:', error);
        setTechnicians([]);
        setAppointments([]);
        setTimeOffEvents([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  const allowTechnicianChoice = import.meta.env?.VITE_ALLOW_TECHNICIAN_CHOICE === 'true';

  const urgencies = [
    { id: 'within-hour', name: "Sous 2h" },
    { id: 'today', name: "Aujourd'hui" },
    { id: 'tomorrow', name: 'Choisir une date' },
  ];

  const selectedUrgency = urgencies.find(
    (u) => u.id === data?.urgency?.id
  );

  const canChangeDate = selectedUrgency?.id === 'tomorrow';

  const getLockedDate = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  useEffect(() => {
    if (!canChangeDate) {
      const today = getLockedDate();
      setSelectedDate(today);
      setCalendarMonth(new Date(today.getFullYear(), today.getMonth(), 1));
      setSelectedSlot(null);
      setSelectedTechnician(null);
      return;
    }

    const min = getLockedDate();
    min.setDate(min.getDate() + 1);

    let next = null;
    if (data?.timeSlot?.date) {
      const saved = new Date(data.timeSlot.date);
      saved.setHours(0, 0, 0, 0);
      if (saved >= min) next = saved;
    }
    if (!next) {
      next = selectedDate && selectedDate >= min ? selectedDate : min;
    }

    setSelectedDate(next);
    setCalendarMonth(new Date(next.getFullYear(), next.getMonth(), 1));
  }, [selectedUrgency?.id, canChangeDate, data?.timeSlot?.date]);

  const toDateKey = (d) => {
    const x = new Date(d);
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };

  const isPastDay = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = new Date(d);
    day.setHours(0, 0, 0, 0);
    return day < today;
  };

  const isDateDisabled = (d) => {
    if (!d) return true;
    if (isPastDay(d)) return true;
    if (canChangeDate) {
      const today = getLockedDate();
      return toDateKey(d) === toDateKey(today);
    }
    return false;
  };

  const buildCalendarDays = () => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const first = new Date(year, month, 1);
    const startPad = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const cells = [];

    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) {
      cells.push(new Date(year, month, day));
    }
    return cells;
  };

  const handleDateSelect = (day) => {
    if (!canChangeDate || isDateDisabled(day)) return;
    const next = new Date(day);
    next.setHours(0, 0, 0, 0);
    setSelectedDate(next);
    setCalendarMonth(new Date(next.getFullYear(), next.getMonth(), 1));
    setSelectedSlot(null);
    setSelectedTechnician(null);
    setError('');
  };

  const shiftCalendarMonth = (delta) => {
    setCalendarMonth((prev) => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + delta);
      return next;
    });
  };

  // Fonction utilitaire pour parser le temps avec validation — voir scheduleAvailability.js

  // Vérifier si un technicien est en congé pour une date/heure donnée
  const isTechnicianOnTimeOffForSlot = (technicianId, date, time = null) =>
    isTechnicianOnTimeOff(technicianId, date, timeOffEvents, time);

  // Vérifier si un technicien spécifique est disponible (incluant les congés)
  const isTechnicianAvailableForSlot = (technicianId, slot) => {
    const slotTimeParts = slot?.time?.split(' - ');
    if (!slotTimeParts || slotTimeParts.length !== 2) return true;
    
    const slotStartTime = parseTimeString(slotTimeParts[0]?.trim());
    const slotEndTime = parseTimeString(slotTimeParts[1]?.trim());
    
    if (!slotStartTime || !slotEndTime) return true;
    
    const slotDate = new Date(slot.date);
    const slotStart = new Date(slotDate);
    slotStart.setHours(slotStartTime.hour, slotStartTime.minute, 0, 0);
    
    const slotEnd = new Date(slotDate);
    slotEnd.setHours(slotEndTime.hour, slotEndTime.minute, 0, 0);

    // 1. Vérifier si le technicien est en congé
    if (isTechnicianOnTimeOffForSlot(technicianId, slotDate, slotStartTime)) {
      return false;
    }

    // 2. Vérifier tous les rendez-vous de ce technicien
    const technicianAppointments = appointments.filter(apt => apt.technician === technicianId);
    
    for (const appt of technicianAppointments) {
      if (!appt.scheduled_date || !appt.scheduled_time_start || !appt.scheduled_time_end) continue;

      const apptStartTime = parseTimeString(appt.scheduled_time_start);
      const apptEndTime = parseTimeString(appt.scheduled_time_end);
      
      if (!apptStartTime || !apptEndTime) continue;

      const apptDate = new Date(appt.scheduled_date);
      const apptStart = new Date(apptDate);
      apptStart.setHours(apptStartTime.hour, apptStartTime.minute, 0, 0);

      const apptEnd = new Date(apptDate);
      apptEnd.setHours(apptEndTime.hour, apptEndTime.minute, 0, 0);

      // Vérifier si ce rendez-vous est le même jour que le créneau
      const isSameDay = apptDate.toDateString() === slotDate.toDateString();
      
      if (isSameDay) {
        const overlaps = apptStart < slotEnd && apptEnd > slotStart;
        if (overlaps) {
          return false; // Technicien occupé
        }
      }
    }
    
    return true; // Technicien disponible
  };

  const slotContext = { technicians, appointments, timeOffEvents };
  const durationMinutes = data?.service?.estimatedDuration || 60;

  const slotsWithTechnicians = isLoading
    ? []
    : getAvailableSlots({
        forDate: selectedDate,
        urgencyId: selectedUrgency?.id,
        durationMinutes,
        ...slotContext,
      });

  // Liste des techniciens mockés pour l'affichage
  const mockTechnicians = [
    {
      id: 'tech-1',
      name: 'Jean Dupont',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_10a10f3da-1763296183354.png",
      avatarAlt: 'Professional headshot of French male plumber with short brown hair wearing blue work uniform',
      rating: 4.9,
      completedJobs: 247,
      specialties: ['Débouchage', 'Réparation fuite']
    },
    {
      id: 'tech-2',
      name: 'Marie Laurent',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_186a7b615-1763293775329.png",
      avatarAlt: 'Professional headshot of French female plumber with blonde hair tied back wearing blue work uniform',
      rating: 4.8,
      completedJobs: 189,
      specialties: ['Installation', 'Chauffage']
    },
    {
      id: 'tech-3',
      name: 'Pierre Martin',
      avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1b82ab60e-1763296079393.png",
      avatarAlt: 'Professional headshot of French male plumber with gray hair and beard wearing blue work uniform',
      rating: 5.0,
      completedJobs: 312,
      specialties: ['Diagnostic', 'Réparation']
    }
  ];
const handleSlotSelect = (slot) => {
  setSelectedSlot(slot);
  setError('');
  handleSubmit(slot, selectedTechnician); // passer le slot et le technicien actuel
};

const handleTechnicianSelect = (technician) => {
  setSelectedTechnician(technician);
  handleSubmit(selectedSlot, technician); // passer le slot actuel et le technicien sélectionné
};

const handleSubmit = (slotParam, technicianParam) => {
  const slotToSubmit = slotParam || selectedSlot;
  const technicianToSubmit = technicianParam || selectedTechnician;

  if (!slotToSubmit) {
    setError('Veuillez sélectionner un créneau horaire pour continuer');
    return;
  }

  onUpdate({
    timeSlot: slotToSubmit,
    technician: showTechnicianChoice ? technicianToSubmit : null,
  });

  onNext(true);
};
  if (isLoading) {
    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Chargement des créneaux disponibles...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="bg-card rounded-lg shadow-md p-6 lg:p-8">
        <div className="flex items-start gap-4  ">
          {/* <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
               <Button variant="outline" size="sm" iconName="ArrowLeft" onClick={onBack}>
                                        
                                      </Button>
          </div> */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              {heading ||
                (canChangeDate
                  ? 'Choisissez votre date d’intervention'
                  : 'Choisissez votre horaire')}
            </h2>
            <p className="text-muted-foreground">
              {subheading ||
                (canChangeDate
                  ? 'Sélectionnez le jour et l’horaire qui vous conviennent.'
                  : `Choisissez l’horaire qui vous convient.`)}
            </p>
          </div>
        </div>
  {/* <StepActions isActive={isActive} className="flex justify-between pt-6 pb-6 sm:pt-8">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
        </StepActions> */}
               <div className="flex justify-between pt-6  border-t border-border">
</div>
        <div className="space-y-6">
          <div>
            {/* <h3 className="text-lg font-semibold text-foreground mb-3">
              Quand souhaitez-vous que nous intervenions ?
            </h3> */}

            {canChangeDate ? (
              <div className="mb-6 rounded-xl border border-border p-4 bg-card">
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    onClick={() => shiftCalendarMonth(-1)}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    aria-label="Mois précédent"
                  >
                    <Icon name="ChevronLeft" size={20} />
                  </button>
                  <p className="text-sm font-semibold text-foreground capitalize">
                    {calendarMonth.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
                  </p>
                  <button
                    type="button"
                    onClick={() => shiftCalendarMonth(1)}
                    className="p-2 rounded-lg hover:bg-muted transition-colors"
                    aria-label="Mois suivant"
                  >
                    <Icon name="ChevronRight" size={20} />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map((d) => (
                    <div key={d} className="text-center text-[11px] font-medium text-muted-foreground py-1">
                      {d}
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {buildCalendarDays().map((day, idx) => {
                    if (!day) return <div key={`empty-${idx}`} />;
                    const selected = toDateKey(day) === toDateKey(selectedDate);
                    const disabled = isDateDisabled(day);
                    return (
                      <button
                        key={toDateKey(day)}
                        type="button"
                        disabled={disabled}
                        onClick={() => handleDateSelect(day)}
                        className={`aspect-square rounded-lg text-sm font-medium transition-colors ${
                          selected
                            ? 'bg-primary text-primary-foreground'
                            : disabled
                            ? 'text-muted-foreground/40 cursor-not-allowed'
                            : 'hover:bg-primary/10 text-foreground'
                        }`}
                      >
                        {day.getDate()}
                      </button>
                    );
                  })}
                </div>

                <p className="text-xs text-muted-foreground mt-3">
                  Date sélectionnée :{' '}
                  <span className="font-medium text-foreground">
                    {selectedDate.toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                  </span>
                </p>
              </div>
            ) : (
              <div className="mb-6 rounded-xl border border-border p-4 bg-muted/30 flex items-center gap-3">
                <Icon name="Calendar" size={20} className="text-primary shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {selectedDate.toLocaleDateString('fr-FR', {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Date imposée par la priorité — seuls les horaires sont modifiables
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-1">
                  Créneaux disponibles
                </h3>
                <p className="text-sm text-muted-foreground">
                  {canChangeDate
                    ? 'Choisissez un horaire pour la date sélectionnée'
                    : 'Choisissez un horaire pour aujourd’hui'}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4">
              {slotsWithTechnicians?.length === 0 ? (
                <div className="col-span-2 lg:col-span-3 text-center py-8 rounded-xl border border-dashed border-border bg-muted/20">
                  <Icon name="Calendar" size={32} className="mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {canChangeDate
                      ? 'Aucun créneau disponible pour cette date. Choisissez un autre jour.'
                      : 'Aucun créneau disponible aujourd’hui. Essayez une autre priorité ou revenez plus tard.'}
                  </p>
                </div>
              ) : (
                slotsWithTechnicians?.map((slot) => {
                const dateObj = new Date(slot.date);
                const isSelected = selectedSlot?.id === slot.id;
                const isAvailable = slot.available && slot.techniciansAvailable > 0;

                return (
                  <button
                    key={slot.id}
                    onClick={() => handleSlotSelect(slot)}
                    disabled={!isAvailable}
                    className={`group relative p-3 sm:p-4 lg:p-5 rounded-lg sm:rounded-xl border-2 transition-all duration-300 overflow-hidden ${
                      isSelected
                        ? 'border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-transparent shadow-md sm:shadow-lg shadow-primary/10 scale-[1.01] sm:scale-[1.02]'
                        : isAvailable
                        ? 'border-border hover:border-primary/50 bg-card hover:shadow-md hover:scale-[1.01]'
                        : 'border-border bg-muted/50 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    {isAvailable && (
                      <div className={`absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 ${
                        isSelected ? 'opacity-100' : 'group-hover:opacity-50'
                      }`} />
                    )}

                    <div className="relative z-10">
                      <div className="flex items-center justify-between mb-2 sm:mb-4">
                        <div className={`w-7 h-7 sm:w-10 sm:h-10 rounded-md sm:rounded-lg flex items-center justify-center transition-all duration-300 ${
                          isSelected
                            ? 'bg-primary/20 shadow-sm'
                            : isAvailable
                            ? 'bg-muted group-hover:bg-primary/10'
                            : 'bg-muted/50'
                        }`}>
                          <Icon
                            name="Clock"
                            size={16}
                            color={isSelected ? 'var(--color-primary)' : 'currentColor'}
                            className={`sm:hidden transition-transform duration-300 ${isAvailable ? 'group-hover:scale-110' : ''}`}
                          />
                          <Icon
                            name="Clock"
                            size={20}
                            color={isSelected ? 'var(--color-primary)' : 'currentColor'}
                            className={`hidden sm:block transition-transform duration-300 ${isAvailable ? 'group-hover:scale-110' : ''}`}
                          />
                        </div>

                        {isSelected && (
                          <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full bg-primary/20 flex items-center justify-center animate-fade-in">
                            <Icon name="CheckCircle" size={16} color="var(--color-primary)" className="sm:hidden" />
                            <Icon name="CheckCircle" size={20} color="var(--color-primary)" className="hidden sm:block" />
                          </div>
                        )}
                      </div>

                      <div className="text-left space-y-1 sm:space-y-2">
                        <p className={`text-xs sm:text-base font-semibold transition-colors duration-200 leading-snug ${
                          isSelected ? 'text-primary' : 'text-foreground'
                        }`}>
                          <span className="sm:hidden">
                            {dateObj.toLocaleDateString('fr-FR', {
                              weekday: 'short',
                              day: 'numeric',
                              month: 'short',
                            })}
                          </span>
                          <span className="hidden sm:inline">
                            {dateObj.toLocaleDateString('fr-FR', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                            })}
                          </span>
                        </p>

                        <div className="flex items-center gap-1.5 sm:gap-2">
                          <div className="w-1 h-3 sm:h-4 bg-primary/30 rounded-full" />
                          <p className="text-xs sm:text-sm font-medium text-muted-foreground">
                            {slot.time}
                          </p>
                        </div>

                        <div className={`flex items-center gap-1 sm:gap-1.5 text-[10px] sm:text-xs mt-1.5 sm:mt-3 ${
                          slot.techniciansAvailable > 0 ? 'text-success' : 'text-error'
                        }`}>
                          <Icon name="Users" size={12} className="sm:hidden" />
                          <Icon name="Users" size={14} className="hidden sm:block" />
                          <span>
                            <span className="sm:hidden">{slot.techniciansAvailable} dispo</span>
                            <span className="hidden sm:inline">
                              {slot.techniciansAvailable} technicien{slot.techniciansAvailable > 1 ? 's' : ''} disponible{slot.techniciansAvailable > 1 ? 's' : ''}
                            </span>
                          </span>
                        </div>
                      </div>

                      <div className={`mt-2 sm:mt-4 h-0.5 sm:h-1 rounded-full transition-all duration-300 ${
                        isSelected
                          ? 'bg-gradient-to-r from-primary via-primary/50 to-transparent w-full'
                          : isAvailable
                          ? 'bg-muted w-0 group-hover:w-1/2'
                          : 'bg-muted/30 w-0'
                      }`} />
                    </div>
                  </button>
                );
              })
              )}
            </div>

            {error && (
              <div className="mt-4 flex items-center gap-3 p-4 bg-error/10 border-2 border-error/20 rounded-xl animate-fade-in">
                <div className="w-10 h-10 rounded-lg bg-error/20 flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertCircle" size={20} color="var(--color-error)" />
                </div>
                <span className="text-sm font-medium text-error">{error}</span>
              </div>
            )}
          </div>

          {selectedSlot && allowTechnicianChoice && (
            <div className="animate-fade-in">
              <div className="p-5 bg-card border-2 border-border rounded-xl hover:border-primary/30 transition-colors duration-300">
                <Checkbox
                  label="Je souhaite choisir mon technicien"
                  description="Optionnel - Vous pouvez sélectionner un technicien spécifique"
                  checked={showTechnicianChoice}
                  onChange={(e) => {
                    setShowTechnicianChoice(e?.target?.checked);
                    if (!e?.target?.checked) {
                      setSelectedTechnician(null);
                    }
                  }}
                />
              </div>

              {showTechnicianChoice && (
                <div className="mt-6 animate-fade-in">
                  <h3 className="text-xl font-semibold text-foreground mb-5 flex items-center gap-2">
                    <div className="w-1 h-6 bg-primary rounded-full" />
                    Techniciens disponibles
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {mockTechnicians?.map((tech) => {
                      const isSelected = selectedTechnician?.id === tech?.id;
                      // Vérifier si ce technicien est disponible pour le créneau sélectionné
                      const isAvailable = selectedSlot && isTechnicianAvailableForSlot(tech.id, selectedSlot);
                      
                      return (
                        <button
                          key={tech?.id}
                          onClick={() => isAvailable && handleTechnicianSelect(tech)}
                          disabled={!isAvailable}
                          className={`group relative p-5 rounded-xl border-2 transition-all duration-300 text-left overflow-hidden ${
                            !isAvailable ? 'opacity-50 cursor-not-allowed border-border bg-muted/30' :
                            isSelected
                              ? 'border-primary bg-gradient-to-br from-primary/10 via-primary/5 to-transparent shadow-lg shadow-primary/10 scale-[1.02]'
                              : 'border-border hover:border-primary/50 bg-card hover:shadow-md hover:scale-[1.01]'
                          }`}
                        >
                          {/* Decorative gradient */}
                          {isAvailable && (
                            <div className={`absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-300 ${
                              isSelected ? 'opacity-100' : 'group-hover:opacity-50'
                            }`} />
                          )}

                          {/* Content */}
                          <div className="relative z-10">
                            <div className="flex items-start gap-3 mb-4">
                              <div className={`w-14 h-14 rounded-full overflow-hidden flex-shrink-0 ring-2 transition-all duration-300 ${
                                isSelected ? 'ring-primary shadow-md' : 
                                isAvailable ? 'ring-border group-hover:ring-primary/30' : 'ring-border'
                              }`}>
                                <Image
                                  src={tech?.avatar}
                                  alt={tech?.avatarAlt}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <h4 className={`font-semibold mb-1.5 transition-colors duration-200 ${
                                  isSelected ? 'text-primary' : 
                                  isAvailable ? 'text-foreground' : 'text-muted-foreground'
                                }`}>
                                  {tech?.name}
                                </h4>
                                <div className="flex items-center gap-1.5">
                                  <div className="flex items-center gap-0.5 px-2 py-0.5 bg-warning/10 rounded-full">
                                    <Icon name="Star" size={12} color="var(--color-warning)" />
                                    <span className="text-xs font-semibold text-warning">{tech?.rating}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {tech?.completedJobs} interventions
                                  </span>
                                </div>
                              </div>

                              {isSelected && (
                                <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center animate-fade-in">
                                  <Icon name="CheckCircle" size={18} color="var(--color-primary)" />
                                </div>
                              )}
                            </div>

                            <div className="flex flex-wrap gap-1.5">
                              {tech?.specialties?.map((specialty, index) => (
                                <span
                                  key={index}
                                  className={`text-xs px-2.5 py-1 rounded-full transition-colors duration-200 ${
                                    isSelected 
                                      ? 'bg-primary/10 text-primary border border-primary/20' 
                                      : isAvailable
                                      ? 'bg-muted text-muted-foreground group-hover:bg-primary/5'
                                      : 'bg-muted/50 text-muted-foreground'
                                  }`}
                                >
                                  {specialty}
                                </span>
                              ))}
                            </div>

                            {!isAvailable && (
                              <div className="mt-2 text-xs text-error">
                                Indisponible pour ce créneau
                              </div>
                            )}

                            {/* Bottom accent */}
                            <div className={`mt-4 h-1 rounded-full transition-all duration-300 ${
                              isSelected
                                ? 'bg-gradient-to-r from-primary via-primary/50 to-transparent w-full'
                                : isAvailable
                                ? 'bg-muted w-0 group-hover:w-2/3'
                                : 'bg-muted/30 w-0'
                            }`} />
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="relative p-5 bg-gradient-to-r from-primary/5 to-primary/0 border-2 border-primary/20 rounded-xl overflow-hidden group hover:shadow-md transition-all duration-300">
            {/* Decorative corner */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full" />
            
            <div className="relative z-10 flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                <Icon name="Bell" size={20} className="text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground mb-2">Rappels automatiques</p>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Vous recevrez un rappel la veille et 2h avant votre rendez-vous. Un système anti-absence vous notifiera en cas de besoin.
                </p>
              </div>
            </div>
          </div>
        </div>

        <StepActions isActive={isActive} showBack={showBack} className="flex justify-between pt-8 mt-8 border-t border-border">
          <Button variant="outline" size="lg" iconName="ArrowLeft" onClick={onBack}>
            Retour
          </Button>
          {/* <Button
            variant="default"
            size="lg"
            iconName="ArrowRight"
            iconPosition="right"
            onClick={handleSubmit}
            disabled={!selectedSlot}
          >
            Continuer
          </Button> */}
        </StepActions>
      </div>
    </div>
  );
};

export default ScheduleStep;